import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import { features, type FeatureItem } from "../data/content";
import { cn } from "../lib/utils";

type Accent = FeatureItem["accent"];

interface AccentStyles {
  readonly wrap: string;
  readonly ring: string;
  readonly icon: string;
  readonly accent: string;
  readonly decor: ReactNode;
}

const ACCENT_STYLES: Record<Accent, AccentStyles> = {
  primary: {
    wrap: "bg-primary-fixed/45",
    ring: "ring-primary-fixed/80",
    icon: "text-primary",
    accent: "bg-primary/85",
    decor: (
      <span
        aria-hidden
        className="absolute -top-3 -right-3 h-14 w-14 rounded-full bg-primary-fixed/40 blur-2xl"
      />
    ),
  },
  secondary: {
    wrap: "bg-secondary-fixed/45",
    ring: "ring-secondary-fixed/80",
    icon: "text-secondary",
    accent: "bg-secondary/85",
    decor: (
      <span
        aria-hidden
        className="absolute -bottom-6 -left-6 h-20 w-20 rounded-full bg-secondary-fixed/55 blur-2xl"
      />
    ),
  },
  tertiary: {
    wrap: "bg-tertiary-fixed/45",
    ring: "ring-tertiary-fixed/80",
    icon: "text-tertiary",
    accent: "bg-tertiary/85",
    decor: (
      <span
        aria-hidden
        className="absolute -top-4 -left-4 h-16 w-16 rounded-full bg-tertiary-fixed/55 blur-2xl"
      />
    ),
  },
};

const SPAN_STYLES: Record<FeatureItem["span"], string> = {
  tall: "lg:row-span-2",
  wide: "lg:col-span-2",
  normal: "",
};

// Vary stagger so the bento reveals with editorial rhythm rather than a metronome.
const STAGGER_MS: Record<number, number> = {
  0: 60,
  1: 140,
  2: 80,
  3: 180,
  4: 100,
  5: 220,
};

export function Features(): JSX.Element {
  return (
    <section
      id="funktionen"
      aria-labelledby="features-title"
      className="relative overflow-hidden bg-surface-container py-20 sm:py-24 lg:py-28"
    >
      {/* Editorial paper-grain backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(23,49,36,0.045) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="container-welope">
        <div className="bloom-on-scroll mx-auto max-w-3xl text-center">
          <p className="section-eyebrow">Funktionen</p>
          <h2
            id="features-title"
            className="mt-3 font-headline text-headline-xl text-primary sm:text-[48px] sm:leading-[1.06] sm:tracking-[-0.02em]"
          >
            Sechs Funktionen, ein Zuhause für dein Tier.
          </h2>
          <p className="mt-5 font-body text-body-lg leading-[1.55] text-on-surface-variant">
            Kein Feature-Overload. Wir konzentrieren uns auf das, was Tierhalter wirklich
            brauchen, verbunden durch ein warmes, ruhiges Design.
          </p>
        </div>

        <div className="mt-16 grid auto-rows-[minmax(220px,auto)] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((feature, idx) => (
            <FeatureCard
              key={feature.id}
              feature={feature}
              delayMs={STAGGER_MS[idx] ?? idx * 80}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureCard({
  feature,
  delayMs,
}: {
  feature: FeatureItem;
  delayMs: number;
}): JSX.Element {
  const accent = ACCENT_STYLES[feature.accent];
  const { Icon } = feature;

  return (
    <article
      className={cn(
        "bloom-on-scroll card-bloom group relative overflow-hidden p-md",
        "ring-1 ring-inset transition-all duration-300 ease-out-expo",
        "hover:-translate-y-1 hover:shadow-bloom-lg",
        accent.ring,
        SPAN_STYLES[feature.span],
      )}
      style={{ transitionDelay: `${delayMs}ms` }}
    >
      {accent.decor}

      <div className={cn("relative flex h-12 w-12 items-center justify-center rounded-lg", accent.wrap)}>
        <Icon className={cn("h-6 w-6 transition-transform group-hover:scale-110", accent.icon)} strokeWidth={1.6} aria-hidden="true" />
      </div>

      <h3 className="relative mt-5 font-headline text-headline-md leading-tight text-on-surface">
        {feature.title}
      </h3>
      <p className="relative mt-2 font-body text-body-md leading-[1.55] text-on-surface-variant">
        {feature.description}
      </p>
      {feature.href && (
        <a
          href={feature.href}
          className="relative mt-3 inline-flex items-center gap-1 font-body text-label-sm font-medium text-primary hover:underline"
        >
          Mehr erfahren
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}
    </article>
  );
}
